import {ArrowBack} from '@mui/icons-material';
import {Box, Button, CircularProgress, Stack, Typography} from '@mui/material';
import {ExamModule} from '@cp/model';
import {QuestionFormData} from '../../../types/QuestionFormData';
import GeneratePromptDialog from '../../../components/PromptDialogs/GeneratePromptDialog';
import GraphJsonPromptDialog from '../../../components/PromptDialogs/GraphJsonPromptDialog';
import PasteQuestionDialog from '../../../components/PromptDialogs/PasteQuestionDialog';

export interface AdminQuestionNewHeaderProps {
  onBack: () => void;
  openGenerateDialog: boolean;
  setOpenGenerateDialog: (open: boolean) => void;
  openGraphJsonPromptDialog: boolean;
  setOpenGraphJsonPromptDialog: (open: boolean) => void;
  openPasteQuestionDialog: boolean;
  setOpenPasteQuestionDialog: (open: boolean) => void;
  examModules: ExamModule[];
  modulesLoading: boolean;
  formData: QuestionFormData;
  handleApply: (formData: Partial<QuestionFormData>) => void;
  handleGenerateClick: () => void;
  onCheckWithRules: () => void;
  onCheckWithGpt: () => void;
  onGenerateGraphJson: () => void;
  checkRulesLoading: boolean;
  checkGptLoading: boolean;
  generateGraphLoading: boolean;
  createLoading: boolean;
}

export default function AdminQuestionNewHeader({
  onBack,
  openGenerateDialog,
  setOpenGenerateDialog,
  openGraphJsonPromptDialog,
  setOpenGraphJsonPromptDialog,
  openPasteQuestionDialog,
  setOpenPasteQuestionDialog,
  examModules,
  modulesLoading,
  formData,
  handleApply,
  handleGenerateClick,
  onCheckWithRules,
  onCheckWithGpt,
  onGenerateGraphJson,
  checkRulesLoading,
  checkGptLoading,
  generateGraphLoading,
  createLoading,
}: AdminQuestionNewHeaderProps) {
  return (
    <Stack spacing={3}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <Button startIcon={<ArrowBack />} onClick={onBack} sx={{minWidth: 'auto'}}>
          Back
        </Button>
      </Box>

      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        {/* Question Actions Group */}
        <Box>
          <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600, color: '#1B263B'}}>
            Question Actions
          </Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <Button
              variant="outlined"
              onClick={handleGenerateClick}
              sx={{
                borderColor: '#1B263B',
                color: '#1B263B',
                '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
              }}
            >
              Generate with Prompt
            </Button>

            <Button
              variant="outlined"
              onClick={() => setOpenPasteQuestionDialog(true)}
              sx={{
                borderColor: '#1B263B',
                color: '#1B263B',
                '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
              }}
            >
              Paste Question JSON
            </Button>

            <Button
              onClick={onCheckWithRules}
              variant="outlined"
              disabled={checkRulesLoading || checkGptLoading || createLoading}
              sx={{
                borderColor: '#1B263B',
                color: '#1B263B',
                '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
              }}
            >
              {checkRulesLoading ? <CircularProgress size={20} /> : 'Check with Rules'}
            </Button>

            <Button
              onClick={onCheckWithGpt}
              variant="outlined"
              disabled={checkRulesLoading || checkGptLoading || createLoading}
              sx={{
                borderColor: '#1B263B',
                color: '#1B263B',
                '&:hover': {borderColor: '#1B263B', bgcolor: '#1B263B', color: 'white'},
              }}
            >
              {checkGptLoading ? <CircularProgress size={20} /> : 'Check with GPT'}
            </Button>
          </Box>
        </Box>

        {/* Graph JSON Actions Group */}
        <Box>
          <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 600, color: '#FF6F59'}}>
            Graph JSON Actions
          </Typography>
          <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
            <Button
              variant="outlined"
              onClick={() => setOpenGraphJsonPromptDialog(true)}
              sx={{
                borderColor: '#FF6F59',
                color: '#FF6F59',
                '&:hover': {borderColor: '#FF6F59', bgcolor: '#FF6F59', color: 'white'},
              }}
            >
              Generate Graph JSON Prompt
            </Button>

            <Button
              onClick={onGenerateGraphJson}
              variant="outlined"
              disabled={generateGraphLoading || createLoading}
              sx={{
                borderColor: '#FF6F59',
                color: '#FF6F59',
                '&:hover': {borderColor: '#FF6F59', bgcolor: '#FF6F59', color: 'white'},
              }}
            >
              {generateGraphLoading ? <CircularProgress size={20} /> : 'Generate Graph JSON'}
            </Button>
          </Box>
        </Box>
      </Box>

      <GeneratePromptDialog
        open={openGenerateDialog}
        examModules={examModules}
        modulesLoading={modulesLoading}
        onClose={() => setOpenGenerateDialog(false)}
        onApply={(parsedQuestion, moduleId, topic, difficulty) =>
          handleApply({
            ...parsedQuestion,
            moduleId,
            topic,
            difficulty,
          })
        }
      />

      <GraphJsonPromptDialog
        open={openGraphJsonPromptDialog}
        formData={formData}
        onClose={() => setOpenGraphJsonPromptDialog(false)}
      />

      <PasteQuestionDialog
        open={openPasteQuestionDialog}
        onClose={() => setOpenPasteQuestionDialog(false)}
        onApply={handleApply}
      />
    </Stack>
  );
}
